import { useCallback, useEffect, useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { Box, Stack } from '@lyearn/ui';
import { useAccessControl } from '@/components/AccessControl';
import { FilterOption } from '@/components/Filter/types';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import { WorkspaceActions, WorkspaceSubjects } from '@/types/schema';

import { ALL_SUBWORKSPACE_ID } from '../../const';
import useSubworkspaces from '../../hooks/useSubworkspaces';
import AddWorkspace from '../AddWorkspace';
import SelectedWorkspaces from '../SelectedWorkspaces';
import RadioItem from './RadioItem';

export type WorkspaceSubjectType = 'content' | 'event' | 'tag' | 'user-survey' | 'gamification';

type AddWorkspaceSettingsProps = {
  selectedValues?: string[] | null;
  onChange: (val: string[]) => void;
  EmptyState?: JSX.Element;
  onRemove: (id: string) => void;
  type?: WorkspaceSubjectType;
  disabled?: boolean;
};

function getWorkspaceSubject(type: WorkspaceSubjectType) {
  switch (type) {
    case 'tag':
      return WorkspaceSubjects.TagSubworkspace;
    case 'event':
    case 'user-survey':
      return WorkspaceSubjects.EventSubworkspace;
    case 'content':
    case 'gamification':
    default:
      return WorkspaceSubjects.ContentSubworkspace;
  }
}

function AddWorkspaceSettings(props: AddWorkspaceSettingsProps) {
  const { selectedValues, onChange, EmptyState, onRemove, type = 'content', disabled } = props;
  const { t } = useTranslation();
  const { subWorkspaces } = useSubworkspaces();
  const user = useUserContext();
  const disableEditActions =
    !useAccessControl(
      user.workspacePermissions || [],
      WorkspaceActions.Manage,
      getWorkspaceSubject(type),
    ) || Boolean(disabled);

  useEffect(() => {
    if (!selectedValues && user.currentSubWorkspace) {
      onChange([user.currentSubWorkspace]);
    }
  }, [onChange, selectedValues, user.currentSubWorkspace]);

  const selectedWorkspaceFilterValues: FilterOption[] = useMemo(
    () =>
      subWorkspaces?.length
        ? subWorkspaces
            .filter((subWorkspace) => selectedValues?.includes(subWorkspace.id))
            .map((item) => ({
              label: item.name,
              value: item.id,
            }))
        : [],
    [selectedValues, subWorkspaces],
  );

  const onWorkspaceFilterChange = useCallback(
    (val: FilterOption[]) => {
      onChange(val.map((item) => item.value));
    },
    [onChange],
  );

  const selectAllWorkpacesEnabled = type === 'tag';

  const isAllWorkspaceSelected =
    selectAllWorkpacesEnabled && selectedValues?.indexOf(ALL_SUBWORKSPACE_ID) !== -1;

  return (
    <>
      {selectAllWorkpacesEnabled ? (
        <Stack className="mb-16 space-y-20 ms-8" direction="col">
          <RadioItem
            checked={isAllWorkspaceSelected}
            disabled={disableEditActions}
            label={t('Workspace:AllWorkspaces')}
            tooltipLabel={t('Workspace:NoPermissionTooltipMessage')}
            onChange={(checked) => {
              if (checked) {
                onChange([ALL_SUBWORKSPACE_ID]);
              }
            }}
          />
          <RadioItem
            checked={!isAllWorkspaceSelected}
            disabled={disableEditActions}
            label={t('Workspace:Specific')}
            tooltipLabel={t('Workspace:NoPermissionTooltipMessage')}
            onChange={(checked) => {
              if (checked) {
                onChange(user.currentSubWorkspace ? [user.currentSubWorkspace] : []);
              }
            }}
          />
        </Stack>
      ) : null}
      {isAllWorkspaceSelected ? null : (
        <Box className={selectAllWorkpacesEnabled ? 'ms-30' : undefined}>
          <AddWorkspace
            EmptyState={EmptyState}
            disabled={disableEditActions}
            selectedValues={selectedWorkspaceFilterValues}
            onChange={onWorkspaceFilterChange}
          />
          <SelectedWorkspaces
            disabled={disableEditActions}
            selectedWorkspaces={selectedWorkspaceFilterValues}
            onRemove={onRemove}
          />
        </Box>
      )}
    </>
  );
}

export default AddWorkspaceSettings;
